import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, RotateCcw, ArrowRight } from "lucide-react";
import { QUIZ, FLAVORS, ASSETS, FLAVOR_IMAGES, SHOP_OPEN } from "../lib/content";
import { useCart } from "../lib/cart";
import { ShareButton } from "../components/ShareButton";

export default function QuizPage({ onJoin }) {
  const [step, setStep] = useState(-1);
  const [scores, setScores] = useState({});
  const { add, setOpen } = useCart();

  const pick = (opt) => {
    const next = { ...scores };
    Object.entries(opt.scores).forEach(([id, pts]) => { next[id] = (next[id] || 0) + pts; });
    setScores(next);
    setStep(step + 1);
  };

  const restart = () => { setScores({}); setStep(0); };

  const result = step >= QUIZ.length
    ? FLAVORS.find((f) => f.id === Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0]) || FLAVORS[0]
    : null;

  return (
    <main data-testid="quiz-page" className="pt-28 pb-24 px-5 md:px-10 min-h-screen">
      <div className="max-w-[760px] mx-auto">
        <AnimatePresence mode="wait">
          {step === -1 && (
            <motion.div key="intro" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }} className="text-center py-10">
              <img src={ASSETS.mascot} alt="" className="w-24 h-24 rounded-full object-cover ring-2 ring-ember mx-auto mb-8" />
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember mb-4">The Flavor Quiz</p>
              <h1 className="font-display font-black uppercase tracking-tight text-cream text-4xl sm:text-5xl lg:text-6xl leading-[0.92]">
                Which gremlin <span className="font-serif italic font-normal lowercase text-ember">are you?</span>
              </h1>
              <p className="font-mono text-sm text-cream/75 mt-5 max-w-md mx-auto leading-relaxed">
                Five quick questions, no wrong answers. We'll match you to your bag of air-roasted trouble.
              </p>
              <button
                onClick={() => setStep(0)}
                data-testid="quiz-start-button"
                className="mt-9 font-mono text-xs uppercase tracking-widest bg-ember text-void px-8 py-4 rounded-full font-medium hover:bg-emberDark transition-colors"
              >
                Find my flavor →
              </button>
            </motion.div>
          )}

          {step >= 0 && step < QUIZ.length && (
            <motion.div key={`q-${step}`} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
              <div className="flex items-center gap-3 mb-10">
                {QUIZ.map((q, i) => (
                  <span key={q.q} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-ember" : "bg-white/10"}`} />
                ))}
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember mb-3" data-testid="quiz-progress">Question {step + 1} / {QUIZ.length}</p>
              <h2 className="font-display font-black uppercase tracking-tight text-cream text-3xl sm:text-4xl md:text-5xl leading-[0.95]" data-testid="quiz-question">
                {QUIZ[step].q}
              </h2>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {QUIZ[step].options.map((o, i) => (
                  <button
                    key={o.label}
                    onClick={() => pick(o)}
                    data-testid={`quiz-option-${i}`}
                    className="text-left border border-white/15 rounded-lg p-6 bg-ash font-mono text-sm text-cream/80 hover:border-ember hover:text-cream hover:bg-void transition-colors"
                  >
                    <span className="font-display font-black text-ember mr-3">{String.fromCharCode(65 + i)}</span>
                    {o.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {result && (
            <motion.div key="result" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="text-center" data-testid="quiz-result">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Your gremlin is</p>
              <h1 className="font-display font-black uppercase tracking-tight text-cream text-4xl sm:text-5xl lg:text-6xl leading-[0.92] mt-4" data-testid="quiz-result-name">
                {result.name}
              </h1>
              <p className="font-mono text-sm text-cream/75 mt-4 max-w-md mx-auto leading-relaxed">{result.tagline}</p>

              <div className="relative rounded-lg overflow-hidden border border-white/10 bg-void h-[300px] max-w-md mx-auto mt-10">
                <img src={FLAVOR_IMAGES[result.id] || ASSETS.packaging} alt={result.name} className="absolute inset-0 w-full h-full object-cover" />
                <span className="absolute top-0 inset-x-0 h-2" style={{ background: result.accent }} />
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
                <button
                  onClick={() => {
                    if (!SHOP_OPEN) {
                      toast(`${result.name} drops soon. Join the Coffee Club for first dibs.`);
                      onJoin();
                      return;
                    }
                    add(result);
                    toast.success(`${result.name} added to your bag.`);
                    setOpen(true);
                  }}
                  data-testid="quiz-add-button"
                  className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest bg-ember text-void px-6 py-3.5 rounded-full font-medium hover:bg-emberDark transition-colors"
                >
                  {SHOP_OPEN ? (<><Plus size={14} /> Add to bag · $19.99</>) : "Dropping soon. Get first dibs"}
                </button>
                <Link
                  to={`/flavor/${result.id}`}
                  data-testid="quiz-flavor-link"
                  className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-white/15 text-cream/70 px-6 py-3.5 rounded-full hover:border-ember hover:text-ember transition-colors"
                >
                  Meet the bean <ArrowRight size={14} />
                </Link>
                <ShareButton kind="flavor" slug={result.id} title={result.name} />
                <button
                  onClick={restart}
                  data-testid="quiz-retake-button"
                  className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-cream/65 px-4 py-3.5 hover:text-ember transition-colors"
                >
                  <RotateCcw size={13} /> Retake
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
